// Idle-deferred boot scheduling — iPad program P1 (boot long-task queue:
// 5.3s home / 6.1s calendar measured). The shell's onCreate used to open
// every realtime transport (user-socket, presence socket, meet SSE)
// synchronously inside the same boot task that paints first chrome; none
// of that work is needed for first paint. `deferIdleBoot` moves a boot
// off the critical path into the browser's idle time, with two hard
// guarantees:
//
//   1. TIMER BACKSTOP, ALWAYS ARMED. requestIdleCallback is frame-gated —
//      it never fires in a hidden/occluded tab (same failure class as the
//      WAAPI onfinish lesson), and WebKit (the iPad target!) doesn't ship
//      rIC at all. A plain setTimeout at `timeout` ms runs the boot
//      deterministically wherever idle never comes; whichever leg fires
//      first cancels the other.
//   2. DISPOSER CONTRACT. The returned function replaces the boot's own
//      unsubscribe at the shell teardown call sites (`el._xxx?.()`): called
//      before the boot ran it cancels the pending schedule; called after,
//      it invokes the cleanup the boot returned. No teardown/boot race.
//
// Every scheduled boot is stamped with performance marks (same `wsboot:`
// idiom as loadPrivateData.js's boot telemetry — read them via
// performance.getEntriesByType('measure') filtered on 'wsboot:'):
//   wsboot:<name>:queued          mark — scheduled (inside the boot task)
//   wsboot:<name>:start/:end      marks — around the boot's own execution
//   wsboot:<name>:deferred        measure — how far off boot it moved
//   wsboot:<name>                 measure — the boot's own sync cost

// Perf-mark helpers — guarded no-ops wherever the Performance API is
// absent. Exported so app.js stamps its KEPT-EAGER boots (analyzing,
// feature flags) with the same idiom.
export const bootMark = (name) => {
  try {
    if (typeof performance !== 'undefined') performance.mark(name)
  } catch (_) {
    /* telemetry only */
  }
}

export const bootMeasure = (name, from, to) => {
  try {
    if (typeof performance !== 'undefined') performance.measure(name, from, to)
  } catch (_) {
    /* telemetry only */
  }
}

// Backstop deadline. Idle usually fires within a few hundred ms of boot;
// 2500ms bounds the worst case (hidden tab timer throttling aligns to
// ~1s ticks at load, so even background boots land ~2.5-3.5s in).
export const IDLE_BOOT_TIMEOUT_MS = 2500

/**
 * Schedule a non-critical boot into idle time.
 *
 * @param {string} name  telemetry name — marks land as `wsboot:<name>*`
 * @param {() => (void|(() => void))} fn  the boot; may return a cleanup fn
 * @param {{ timeout?: number }} [opts]  backstop deadline override (ms)
 * @returns {() => void} disposer — cancels a pending boot, or runs the
 *   boot's returned cleanup when it already executed. Idempotent.
 */
export const deferIdleBoot = (name, fn, { timeout = IDLE_BOOT_TIMEOUT_MS } = {}) => {
  let ran = false
  let disposed = false
  let cleanup = null
  let idleId = null
  let timerId = null

  bootMark(`wsboot:${name}:queued`)

  const cancelPending = () => {
    if (idleId != null && typeof cancelIdleCallback === 'function') {
      try {
        cancelIdleCallback(idleId)
      } catch (_) {}
    }
    if (timerId != null) clearTimeout(timerId)
    idleId = null
    timerId = null
  }

  const run = () => {
    if (ran || disposed) return
    ran = true
    cancelPending()
    bootMark(`wsboot:${name}:start`)
    try {
      const result = fn()
      cleanup = typeof result === 'function' ? result : null
    } catch (err) {
      console.warn(`[shell] idle boot '${name}' failed:`, err?.message || err)
    }
    bootMark(`wsboot:${name}:end`)
    bootMeasure(`wsboot:${name}:deferred`, `wsboot:${name}:queued`, `wsboot:${name}:start`)
    bootMeasure(`wsboot:${name}`, `wsboot:${name}:start`, `wsboot:${name}:end`)
  }

  // Idle leg — only where the API exists (WebKit/WKWebView lacks it).
  if (typeof requestIdleCallback === 'function') {
    idleId = requestIdleCallback(run, { timeout })
  }
  // Backstop leg — ALWAYS armed (rule 1 above). run() dedupes.
  timerId = setTimeout(run, timeout)

  return () => {
    if (disposed) return
    disposed = true
    cancelPending()
    if (typeof cleanup === 'function') {
      try {
        cleanup()
      } catch (_) {}
    }
    cleanup = null
  }
}
